import React, { useEffect, useRef, useState } from 'react';
import API from '../api';
import styles from './Services.module.css';
import servicesImage from '../images/Services.jpg';
import PageHeading from '../components/PageHeading';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const Services = () => {
  const { user } = useAuth();
  const isAdmin = !!user && user.role === 'admin';
  const [text, setText] = useState('');
  const [html, setHtml] = useState('');
  const [format, setFormat] = useState('text');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftHtml, setDraftHtml] = useState('');
  const [draftFormat, setDraftFormat] = useState('text');
  const [font, setFont] = useState('');
  const [draftFont, setDraftFont] = useState('');
  const [colorPick, setColorPick] = useState('#F9DE4B');
  const [sizePick, setSizePick] = useState(20);
  const [fontPick, setFontPick] = useState('');
  const textAreaRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await API.get('/content', { params: { page: 'Services', section: 'servicesContent' } });
        setText(res.data.text || '');
        setHtml(res.data.html || '');
        setFormat(res.data.format || 'text');
        setFont(res.data.font || '');
      } catch (_) {}
    }
    load();
  }, []);

  const escapeHtml = (unsafe) => {
    return (unsafe || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const renderFormattedText = (plainText) => {
    let safe = escapeHtml(plainText || '');
    safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    safe = safe.replace(/\[size=(\d{1,3})\]([\s\S]*?)\[\/size\]/g, (m, p1, p2) => {
      const size = Math.max(8, Math.min(72, parseInt(p1, 10) || 16));
      return `<span style="font-size:${size}px">${p2}</span>`;
    });
    safe = safe.replace(/\[color=([#a-zA-Z0-9]+)\]([\s\S]*?)\[\/color\]/g, (m, p1, p2) => {
      const color = String(p1).slice(0, 32);
      return `<span style="color:${color}">${p2}</span>`;
    });
    // Inline font: [font=Bebas Neue]text[/font]
    safe = safe.replace(/\[font=([a-zA-Z\s\-]+)\]([\s\S]*?)\[\/font\]/g, (m, p1, p2) => {
      const name = String(p1).trim();
      let family = '';
      if (name === 'Bebas Neue') family = "'Bebas Neue', sans-serif";
      else if (name === 'Barlow') family = "'Barlow', sans-serif";
      else if (name.toLowerCase() === 'sans-serif' || name.toLowerCase() === 'sans serif') family = 'sans-serif';
      else family = name;
      return `<span style=\"font-family:${family}\">${p2}</span>`;
    });
    safe = safe
      .replace(/\[ul\]/g, '<ul>')
      .replace(/\[\/ul\]/g, '</ul>')
      .replace(/\[ol\]/g, '<ol>')
      .replace(/\[\/ol\]/g, '</ol>')
      .replace(/\[li\]/g, '<li>')
      .replace(/\[\/li\]/g, '</li>');
    safe = safe.replace(/\n/g, '<br />');
    return { __html: safe };
  };

  const wrapSelection = (before, after) => {
    const el = textAreaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const value = draft;
    const selected = value.slice(start, end) || 'text';
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    setDraft(next);
    setTimeout(() => {
      const newStart = start + before.length;
      const newEnd = newStart + selected.length;
      el.focus();
      el.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const applyBold = () => wrapSelection('**', '**');
  const applyColor = () => wrapSelection(`[color=${colorPick}]`, '[/color]');
  const applySize = () => {
    const px = Math.max(8, Math.min(72, parseInt(sizePick, 10) || 16));
    wrapSelection(`[size=${px}]`, '[/size]');
  };
  const applyCombo = () => {
    const px = Math.max(8, Math.min(72, parseInt(sizePick, 10) || 16));
    const before = `[color=${colorPick}][size=${px}]`;
    const after = '[/size][/color]';
    wrapSelection(before, after);
  };
  const applyUl = () => wrapSelection('[ul]\n', '\n[/ul]');
  const applyOl = () => wrapSelection('[ol]\n', '\n[/ol]');
  const applyLi = () => wrapSelection('[li]', '[/li]');
  return (
    // This wrapper controls the entire page layout
    <div className={styles.pageContainer}>
      {/* HEADER IS NOW A SIBLING TO MAIN */}
      <Header />
      {/* Background behind header and content (desktop+) */}
      <div className={styles.leftSection}>
        <img className={styles.backgroundImageLeft} src={servicesImage} alt="" />
        <div className={styles.imageOverlayLeft} />
      </div>

      {/* MAIN CONTENT AREA */}
      <main className={styles.servicesContainer}>
        <div className={styles.contentLayer}>
          <PageHeading>WHAT WE OFFER</PageHeading>
          <div className={styles.servicesContent}>
          <div className={styles.contentBlock}>
            {editing ? (
              <>
                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={{ marginRight: '1rem' }}>
                    <input type="radio" name="fmt" value="text" checked={draftFormat === 'text'} onChange={() => setDraftFormat('text')} /> Text
                  </label>
                  <label>
                    <input type="radio" name="fmt" value="html" checked={draftFormat === 'html'} onChange={() => setDraftFormat('html')} /> HTML
                  </label>
                </div>
                {draftFormat === 'text' ? (
                  <>
                    <textarea
                      ref={textAreaRef}
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      rows={24}
                      style={{ width: '100%', fontFamily: 'monospace', marginBottom: '0.5rem', minHeight: '50vh', resize: 'vertical' }}
                      placeholder="Enter text"
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <button type="button" className="btn btn-outline" onClick={applyBold}>Bold</button>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label>Color</label>
                        <input type="color" value={colorPick} onChange={e => setColorPick(e.target.value)} />
                        <button type="button" className="btn btn-outline" onClick={applyColor}>Apply Color</button>
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label>Size</label>
                        <input type="number" min={8} max={72} step={1} value={sizePick} onChange={e => setSizePick(e.target.value)} style={{ width: 70 }} />
                        <button type="button" className="btn btn-outline" onClick={applySize}>Apply Size</button>
                      </div>
                      <button type="button" className="btn btn-outline" onClick={applyCombo}>Color + Size</button>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label>Font</label>
                        <select value={fontPick} onChange={e => setFontPick(e.target.value)}>
                          <option value="">Default</option>
                          <option value="Bebas Neue">Bebas Neue</option>
                          <option value="Barlow">Barlow</option>
                          <option value="sans-serif">Sans-serif</option>
                        </select>
                        <button type="button" className="btn btn-outline" onClick={() => wrapSelection(`[font=${fontPick || 'sans-serif'}]`, '[/font]')}>Apply Font</button>
                      </div>
                      <span style={{ width: 1, height: 20, background: '#555' }} />
                      <button type="button" className="btn btn-outline" onClick={applyUl}>UL</button>
                      <button type="button" className="btn btn-outline" onClick={applyOl}>OL</button>
                      <button type="button" className="btn btn-outline" onClick={applyLi}>LI</button>
                    </div>
                  </>
                ) : (
                  <textarea
                    value={draftHtml}
                    onChange={e => setDraftHtml(e.target.value)}
                    rows={24}
                    style={{ width: '100%', fontFamily: 'monospace', marginBottom: '0.5rem', minHeight: '50vh', resize: 'vertical' }}
                    placeholder="Enter HTML"
                  />
                )}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      await API.put('/admin/content', { page: 'Services', section: 'servicesContent', text: draftFormat === 'text' ? draft : '', html: draftFormat === 'html' ? draftHtml : '', format: draftFormat, font: draftFont });
                      setText(draftFormat === 'text' ? draft : '');
                      setHtml(draftFormat === 'html' ? draftHtml : '');
                      setFormat(draftFormat);
                      setFont(draftFont);
                      setEditing(false);
                    }}
                  >Save</button>
                  <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                  <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    <label>Font</label>
                    <select value={draftFont} onChange={e => setDraftFont(e.target.value)}>
                      <option value="">Default</option>
                      <option value="Bebas Neue, sans-serif">Bebas Neue</option>
                      <option value="Barlow, sans-serif">Barlow</option>
                      <option value="sans-serif">Sans-serif</option>
                    </select>
                  </div>
                </div>
              </>
            ) : (
              format === 'html' && html ? (
                <div style={{ fontFamily: font || undefined }} dangerouslySetInnerHTML={{ __html: html }} />
              ) : text ? (
                <div style={{ fontFamily: font || undefined }} dangerouslySetInnerHTML={renderFormattedText(text)} />
              ) : null
            )}
            {isAdmin && !editing && (
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-outline" onClick={() => { setDraft(text); setDraftHtml(html); setDraftFormat(format); setDraftFont(font || ''); setEditing(true); }}>Edit</button>
              </div>
            )}
            </div>
          </div>
        </div>
      </main>
      
      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default Services;